import React, { useState } from 'react';
import { Star, Users, Clock, Mic2, Info, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import { SectionTitle } from './About';
import { Dialog, DialogContent } from './ui/dialog';

const Packages = () => {
  const { t } = useLanguage();
  const p = t.packages;
  const [video, setVideo] = useState(null);
  const go = () => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="packages" className="section-pad bg-gradient-to-b from-[#14120d] via-[#1e1a11] to-[#14120d]">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionTitle eyebrow={p.eyebrow} title={p.title} subtitle={p.subtitle} />
        </Reveal>

        <Reveal delay={80}>
          <div className="text-center mt-8">
            <span className="font-sans-el text-[13px] tracking-[0.25em] uppercase text-[#c2bbaa]">{p.startFrom}</span>
            <div className="font-deco text-4xl gold-gradient-text font-bold mt-2">{p.startPrice}</div>
          </div>
        </Reveal>

        {/* Formations */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 items-stretch">
          {p.items.map((pkg, i) => (
            <Reveal key={pkg.name} delay={i * 80}>
              <div className={`card-lux h-full p-7 flex flex-col relative ${pkg.popular ? 'border-[#e6b83f]/60 lg:-translate-y-4' : ''}`}>
                {pkg.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 btn-gold px-4 py-1.5 rounded-sm text-[10px] tracking-[0.2em] uppercase font-semibold whitespace-nowrap">
                    <Star size={12} /> {p.badge}
                  </span>
                )}
                <h3 className="font-deco text-xl font-semibold text-[#f7f1e3]">{pkg.name}</h3>
                <div className="gold-line w-14 my-4" />
                <div className="flex items-start gap-2.5 text-[#c9c2b3] font-sans-el text-[15px]">
                  <Users size={17} className="text-[#e6b83f] shrink-0 mt-0.5" />
                  <span>{pkg.elements}</span>
                </div>
                <div className="mt-3 space-y-2 flex-1">
                  <span className="font-sans-el text-[12px] tracking-[0.18em] uppercase text-[#a8a190]">{p.durationLabel}</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {pkg.durations.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#e6b83f]/25 text-[14px] text-[#d8d1c1] font-sans-el">
                        <Clock size={13} className="text-[#e6b83f]" /> {d}
                      </span>
                    ))}
                  </div>
                </div>
                <button onClick={go} className={`mt-7 py-3 rounded-sm text-[12px] tracking-[0.18em] uppercase font-medium transition-all ${pkg.popular ? 'btn-gold' : 'btn-ghost-gold'}`}>
                  {p.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Guest artists & special shows */}
        <Reveal>
          <h3 className="font-deco text-2xl sm:text-3xl text-center text-[#f7f1e3] mt-20">{p.showsTitle}</h3>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {p.shows.map((show, i) => (
            <Reveal key={show.name} delay={i * 90}>
              <button
                onClick={() => show.video && setVideo(show.video)}
                className="card-lux w-full text-left h-full p-8 flex gap-5 group cursor-pointer"
              >
                <div className="relative w-12 h-12 rounded-sm border border-[#e6b83f]/40 flex items-center justify-center shrink-0 group-hover:bg-[#e6b83f]/10 transition-colors">
                  <Mic2 size={22} strokeWidth={1.4} className="text-[#f5deA0]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-deco text-2xl text-[#f7f1e3]">{show.name}</h4>
                  <p className="font-sans-el text-[16px] tracking-wide text-[#e6b83f] mt-1.5">{show.subtitle}</p>
                  <p className="font-sans-el text-[17px] text-[#cbc4b3] font-light mt-3 leading-relaxed">{show.details}</p>
                  {show.video && (
                    <span className="inline-flex items-center gap-2 mt-4 font-sans-el text-[13px] tracking-[0.14em] uppercase text-[#f5deA0] group-hover:text-[#f7ecc9] transition-colors">
                      <span className="w-8 h-8 rounded-full bg-[#e6b83f] flex items-center justify-center text-[#14120d] group-hover:scale-110 transition-transform">
                        <Play size={14} fill="currentColor" />
                      </span>
                      {p.watch}
                    </span>
                  )}
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Specs */}
        <Reveal delay={80}>
          <div className="card-lux mt-12 p-8">
            <div className="flex items-center gap-3 mb-5">
              <Info size={18} className="text-[#e6b83f]" />
              <h4 className="font-deco text-lg text-[#f7f1e3]">{p.specsTitle}</h4>
            </div>
            <ul className="grid sm:grid-cols-3 gap-4">
              {p.specs.map((s) => (
                <li key={s} className="font-sans-el text-[14px] text-[#cbc4b3] font-light leading-relaxed border-l border-[#e6b83f]/25 pl-4">{s}</li>
              ))}
            </ul>
          </div>
        </Reveal>

        <p className="text-center font-sans-el text-[13px] tracking-wide text-[#a19a88] mt-10 italic">{p.note}</p>
      </div>

      <Dialog open={!!video} onOpenChange={(o) => !o && setVideo(null)}>
        <DialogContent className="max-w-4xl bg-transparent border-0 p-0 shadow-none">
          {video && (
            <div className="aspect-video w-full">
              <iframe
                className="w-full h-full rounded-sm"
                src={`https://www.youtube.com/embed/${video}?autoplay=1`}
                title="LAJO"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Packages;
